
import axios from 'axios'
import firebase from 'firebase/app'
import 'firebase/auth'
import 'firebase/database'

const DEBI_TYPES = [ //DEBI
    'DPSRRS',
    'OFRS', 
    'OFES',
    'RPRS',
    'VAS'
] as const;
  
// เพิ่ม interface สำหรับ type safety
interface EngagementData {
    company: string;
    companyId: string;
    email: string;
    date: string;
    team: string;
    [key: string]: any; // สำหรับ dynamic properties เช่น OFES_total, OFES_answer
}
  
interface ProcessedEngagementItem {
    company: string;
    email: string;
    total: number;
    answer: number;
    date: string;
    type: string;
    team: string;
}
  
const processEngagementData = (rawData: EngagementData[]): Record<string, ProcessedEngagementItem> => {
    const engagementIndexDay: Record<string, ProcessedEngagementItem> = {};

    for (const data of Object.values(rawData)) {
        for (const type of DEBI_TYPES) {
        const key = `${data.company}-${data.email}-${type}-${data.date}`;
        
        engagementIndexDay[key] = {
            company: data.company,
            email: data.email,
            total: data[`${type}_total`] || 0,
            answer: data[`${type}_answer`] || 0,
            date: data.date,
            type,
            team: data.team
        };
        }
    }

    return engagementIndexDay;
}

const publicJsonHeaders = {
    'Content-Type': 'application/json',
}

const getAuthHeaders = async () => {
    const currentUser = firebase.auth().currentUser
    if (!currentUser) {
        throw new Error('Please sign in again to continue.')
    }

    const token = await currentUser.getIdToken()
    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    }
}

const postWithAuth = async (url: string, data: any) =>
    axios.post(url, data, { headers: await getAuthHeaders() })

const COMPANY_CACHE_TTL_MS = 5 * 60 * 1000
/** Session-friendly TTL for large member lists reused across dashboard/widgets. */
export const MEMBER_DATA_CACHE_TTL_MS = 5 * 60 * 1000
export const QUESTION_METADATA_CACHE_TTL_MS = 30 * 60 * 1000

type CacheEntry<T = any> = {
    expiresAt: number;
    value: T;
}

const companyCache: Record<string, CacheEntry<any>> = {}
const userAllCache: Record<string, CacheEntry<any>> = {}
const teamByCompanyCache: Record<string, CacheEntry<any>> = {}
const reportCardQuestionsCache: Record<string, CacheEntry<any>> = {}
const companyCustomQuestionsCache: Record<string, CacheEntry<any>> = {}

const pendingCompanyRequests: Record<string, Promise<any>> = {}
const pendingUserAllRequests: Record<string, Promise<any>> = {}
const pendingTeamByCompanyRequests: Record<string, Promise<any>> = {}
let pendingReportCardQuestionsRequest: Promise<Record<string, any>> | null = null
const pendingCompanyCustomQuestionsRequests: Record<string, Promise<Record<string, any>>> = {}
const companyCacheVersions: Record<string, number> = {}
const userAllCacheVersions: Record<string, number> = {}
const teamByCompanyCacheVersions: Record<string, number> = {}

const getCachedValue = (cacheStore: Record<string, CacheEntry<any>>, key: string) => {
    const cached = cacheStore[key]

    if (!cached) {
        return null
    }

    if (cached.expiresAt <= Date.now()) {
        delete cacheStore[key]
        return null
    }

    return cached.value
}

const setCachedValue = (
    cacheStore: Record<string, CacheEntry<any>>,
    key: string,
    value: any,
    ttlMs: number = COMPANY_CACHE_TTL_MS,
) => {
    cacheStore[key] = {
        expiresAt: Date.now() + ttlMs,
        value,
    }

    return value
}

export const getCompany = async (company:string) => {
    const cachedCompany = getCachedValue(companyCache, company)
    if (cachedCompany) {
        return cachedCompany
    }

    if (pendingCompanyRequests[company]) {
        return pendingCompanyRequests[company]
    }

    const requestVersion = companyCacheVersions[company] || 0
    const request = axios
        .post(`https://getcompany-bv43tjyzrq-uc.a.run.app`, {company:company}, { headers: await getAuthHeaders() })
        .then((response) => {
            if ((companyCacheVersions[company] || 0) !== requestVersion) {
                return response.data
            }

            return setCachedValue(companyCache, company, response.data)
        })
        .finally(() => {
            if (pendingCompanyRequests[company] === request) {
                delete pendingCompanyRequests[company]
            }
        })
    pendingCompanyRequests[company] = request

    return pendingCompanyRequests[company]
}

export const invalidateCompanyCache = (company: string) => {
    companyCacheVersions[company] = (companyCacheVersions[company] || 0) + 1
    delete companyCache[company]
    delete pendingCompanyRequests[company]
}

export const invalidateUserAllCache = (company: string) => {
    userAllCacheVersions[company] = (userAllCacheVersions[company] || 0) + 1
    delete userAllCache[company]
    delete pendingUserAllRequests[company]
}

export const invalidateTeamByCompanyCache = (company: string) => {
    teamByCompanyCacheVersions[company] = (teamByCompanyCacheVersions[company] || 0) + 1
    delete teamByCompanyCache[company]
    delete pendingTeamByCompanyRequests[company]
}

export const invalidateMemberAndTeamCaches = (company: string) => {
    invalidateUserAllCache(company)
    invalidateTeamByCompanyCache(company)
    invalidateCompanyCustomQuestionsCache(company)
}

export const invalidateCompanyCustomQuestionsCache = (company: string) => {
    delete companyCustomQuestionsCache[company]
    delete pendingCompanyCustomQuestionsRequests[company]
}

export const invalidateReportCardQuestionsCache = () => {
    delete reportCardQuestionsCache.global
    pendingReportCardQuestionsRequest = null
}

const mapQuestionsSnapshot = (snapshot: firebase.database.DataSnapshot) => {
    const result: Record<string, any> = {}
    if (!snapshot.exists()) {
        return result
    }

    snapshot.forEach((doc) => {
        const data = doc.val()
        if (!data || typeof data !== 'object') {
            return
        }
        result[doc.key as string] = { ...data, key: doc.key }
    })
    return result
}

export const getReportCardQuestions = async (): Promise<Record<string, any>> => {
    const cacheKey = 'global'
    const cached = getCachedValue(reportCardQuestionsCache, cacheKey)
    if (cached) {
        return cached
    }

    if (pendingReportCardQuestionsRequest) {
        return pendingReportCardQuestionsRequest
    }

    pendingReportCardQuestionsRequest = firebase.database()
        .ref('questions')
        .orderByChild('report_card')
        .equalTo('Y')
        .once('value')
        .then((snapshot) => setCachedValue(
            reportCardQuestionsCache,
            cacheKey,
            mapQuestionsSnapshot(snapshot),
            QUESTION_METADATA_CACHE_TTL_MS,
        ))
        .finally(() => {
            pendingReportCardQuestionsRequest = null
        })

    return pendingReportCardQuestionsRequest
}

export const getCompanyCustomQuestions = async (companyId: string): Promise<Record<string, any>> => {
    if (!companyId) {
        return {}
    }

    const cached = getCachedValue(companyCustomQuestionsCache, companyId)
    if (cached) {
        return cached
    }

    if (pendingCompanyCustomQuestionsRequests[companyId]) {
        return pendingCompanyCustomQuestionsRequests[companyId]
    }

    pendingCompanyCustomQuestionsRequests[companyId] = firebase.database()
        .ref('questions')
        .orderByChild('company')
        .equalTo(companyId)
        .once('value')
        .then((snapshot) => setCachedValue(
            companyCustomQuestionsCache,
            companyId,
            mapQuestionsSnapshot(snapshot),
            QUESTION_METADATA_CACHE_TTL_MS,
        ))
        .finally(() => {
            delete pendingCompanyCustomQuestionsRequests[companyId]
        })

    return pendingCompanyCustomQuestionsRequests[companyId]
}

export const getTeamByCompany = async (company:string) => {
    const cachedTeams = getCachedValue(teamByCompanyCache, company)
    if (cachedTeams) {
        return cachedTeams
    }

    if(pendingTeamByCompanyRequests[company]){
        return pendingTeamByCompanyRequests[company]
    }

    const requestVersion = teamByCompanyCacheVersions[company] || 0
    const request = axios
        .post(`https://getteamall-bv43tjyzrq-uc.a.run.app`, {company:company}, { headers: await getAuthHeaders() })
        .then((response) => {
            if ((teamByCompanyCacheVersions[company] || 0) !== requestVersion) {
                return response.data
            }

            return setCachedValue(teamByCompanyCache, company, response.data, MEMBER_DATA_CACHE_TTL_MS)
        })
        .finally(() => {
            if (pendingTeamByCompanyRequests[company] === request) {
                delete pendingTeamByCompanyRequests[company]
            }
        })
    pendingTeamByCompanyRequests[company] = request

    return pendingTeamByCompanyRequests[company]
}

export const getByTeam = async (team_id:string) => {
    const response = await postWithAuth(`https://getteam-bv43tjyzrq-uc.a.run.app`, {team:team_id})
    return response.data
}

export const getExcelsDetail = async () => {

    const response = await postWithAuth(`https://getexceldetails-bv43tjyzrq-uc.a.run.app`, '')
    return response.data
}

export const getUserAll = async (company:string) => {
    const cachedUsers = getCachedValue(userAllCache, company)
    if (cachedUsers) {
        return cachedUsers
    }

    if (pendingUserAllRequests[company]) {
        return pendingUserAllRequests[company]
    }

    const requestVersion = userAllCacheVersions[company] || 0
    const request = axios
        .post(`https://getuserall-bv43tjyzrq-uc.a.run.app`, {company:company}, { headers: await getAuthHeaders() })
        .then((response) => {
            if ((userAllCacheVersions[company] || 0) !== requestVersion) {
                return response.data
            }

            return setCachedValue(userAllCache, company, response.data, MEMBER_DATA_CACHE_TTL_MS)
        })
        .finally(() => {
            if (pendingUserAllRequests[company] === request) {
                delete pendingUserAllRequests[company]
            }
        })
    pendingUserAllRequests[company] = request

    return pendingUserAllRequests[company]
}

export const getByUser = async (email:string) => {
    const response = await postWithAuth(`https://getuser-bv43tjyzrq-uc.a.run.app`, {email:email})
    return response.data
}

export const getLoginData = async (email:string) => {
    const emailKey = String(email || '').toLowerCase().trim().replace(/\./g, '||')
    const response = await axios.post(
        `https://us-central1-people2-admin.cloudfunctions.net/getLoginData?email=${encodeURIComponent(emailKey)}`
    )
    return response.data
}

const isDataUrl = (value:any): boolean =>
    typeof value === 'string' && value.indexOf('data:') === 0

/** Keep only the company fields needed to restore a session from localStorage. */
export const slimCompanySnapshot = (company:any, companyKey?:string) => ({
    name: (company && company.name) ? company.name : '',
    key: (company && company.key) ? company.key : (companyKey || ''),
    plan_tier: (company && company.plan_tier) ? company.plan_tier : 'pro',
    onboarding_completed: company && company.onboarding_completed,
})

/** Strip oversized blobs before persisting or re-reading session user data. */
export const sanitizeSessionUser = (user:any) => {
    if (!user || typeof user !== 'object') {
        return user
    }

    const profilepic = isDataUrl(user.profilepic) ? '' : (user.profilepic || '')
    const profilepic_crop = isDataUrl(user.profilepic_crop) ? '' : (user.profilepic_crop || '')
    const profilepic_small = isDataUrl(user.profilepic_small) ? '' : (user.profilepic_small || '')

    return {
        ...user,
        profilepic,
        profilepic_crop,
        profilepic_small,
        companyData: slimCompanySnapshot(user.companyData || {}, user.company),
    }
}

export const encodeSessionUser = (user:any): string =>
    btoa(encodeURIComponent(JSON.stringify(user)))

export const decodeSessionUser = (encoded:any): any => {
    if (!encoded) {
        return null
    }

    const parsed = JSON.parse(decodeURIComponent(atob(encoded)))
    return sanitizeSessionUser(parsed)
}

export const setSessionUserData = (user:any): boolean => {
    const sanitized = sanitizeSessionUser(user)

    try {
        localStorage.setItem('userData', encodeSessionUser(sanitized))
        return true
    } catch (error) {
        try {
            const stripped = sanitizeSessionUser({
                ...sanitized,
                profilepic: '',
                profilepic_crop: '',
                profilepic_small: '',
            })
            localStorage.setItem('userData', encodeSessionUser(stripped))
            return true
        } catch (retryError) {
            console.error('[session] Failed to persist userData:', retryError)
            return false
        }
    }
}

export const buildSessionUser = (sourceUser:any, company:any, isAdviser:boolean = false) => ({
    company_name: (company && company.name) ? company.name : sourceUser.company,
    company: sourceUser.company,
    firstname: sourceUser.firstname,
    lastname: sourceUser.lastname,
    user_type: sourceUser.user_type || (isAdviser ? 'adviser' : ''),
    email: sourceUser.email,
    profilepic: isDataUrl(sourceUser.profilepic) ? '' : (sourceUser.profilepic || ''),
    profilepic_crop: isDataUrl(sourceUser.profilepic_crop) ? '' : (sourceUser.profilepic_crop || ''),
    profilepic_small: isDataUrl(sourceUser.profilepic_small) ? '' : (sourceUser.profilepic_small || ''),
    team: sourceUser.team || '',
    is_adviser: isAdviser,
    // Plan-tier gate (A2) reads these via rootState.authen.user.companyData.plan_tier
    // and rootState.authen.user.onboarding_completed. Defaults keep existing pro
    // customers fully unlocked before the backend backfill lands.
    plan_tier: (company && company.plan_tier) ? company.plan_tier : 'pro',
    onboarding_completed: !(company && company.onboarding_completed === false),
    // Full company objects can exceed the ~5MB localStorage quota; keep a slim snapshot only.
    companyData: slimCompanySnapshot(company, sourceUser.company),
})

export const getByQuestion = async (question_id:any) => {
    const response = await postWithAuth(`https://getquestion-bv43tjyzrq-uc.a.run.app`, {id:question_id})
    return response.data
}

export const getCategory = async () => {
    const response = await postWithAuth(`https://getcategories-bv43tjyzrq-uc.a.run.app`, '')
    return response.data
}

//เอาเฉพาะ company main
export const getQustionAll = async () => {
    const response = await postWithAuth(`https://getquestions-bv43tjyzrq-uc.a.run.app`, '')
    return response.data
}

export const getHerePoint = async (company:string,statusrowValues: any[],date:string) => {
    const response = await postWithAuth(`https://getherepoint-bv43tjyzrq-uc.a.run.app`, {
        "where": [
          {"field": "company", "operator": "==", "value": company},  
          {"field": "statusrow", "operator": "in", "value": statusrowValues},
          {"field": "date", "operator": ">=", "value": date}
        ]
      })
     // console.log('response',response.data)
    return response.data
}

// ---------------------------------------------------------------------------
// Self-serve signup API helpers (Track A3)
// ---------------------------------------------------------------------------
//
// Backs the new `/signup/*` PIN flow and the extended `/freetrial` endpoint
// that provisions a free-tier tenant atomically. See the feature plan at
// docs/plans/.../free-tier-signup-onboarding.md ("PIN-Before-Wizard Flow"
// and "Backend Payload — extensions to /freetrial") for the full contract.
//
// Mock mode: when `process.env.VUE_APP_SIGNUP_MOCK === 'true'`, each helper
// short-circuits and returns synthetic responses after a small delay so the
// signup UI can be built and exercised before the backend endpoints are live.
// ---------------------------------------------------------------------------

const API_NODE = 'https://api-node.happily.ai'
//const API_NODE = 'http://localhost:80'
const SIGNUP_MOCK = process.env.VUE_APP_SIGNUP_MOCK === 'true'

// Small helper so mock paths look/feel like real network calls (200–500ms).
const mockDelay = <T>(value: T, ms?: number): Promise<T> =>
    new Promise((resolve) => {
        const jitter = typeof ms === 'number' ? ms : 200 + Math.floor(Math.random() * 300)
        setTimeout(() => resolve(value), jitter)
    })

// Mock-mode error helper. Shaped loosely like an axios error so callers that
// inspect `err.response?.status` keep working.
const mockHttpError = (status: number, message: string): Error => {
    const err: any = new Error(message)
    err.response = { status, data: { message } }
    err.isMockError = true
    return err
}

export interface RequestSignupPinParams {
    email: string;
    companyName?: string;
}

export interface RequestSignupPinResult {
    ok: boolean;
}

/**
 * POST /signup/request-pin — emails a 4-digit signup PIN.
 * Rate-limited server-side (per-IP 5/hr, per-email 3/hr). Callers should
 * surface 429 to the UI as "too many attempts, try again later".
 */
export const requestSignupPin = async (
    params: RequestSignupPinParams
): Promise<RequestSignupPinResult> => {
    // if (SIGNUP_MOCK) {
    //     return mockDelay({ ok: true })
    // }

    const response = await axios.post(
        `${API_NODE}/signup/request-pin`,
        { email: params.email, companyName: params.companyName || '' },
        { headers: { 'Content-Type': 'application/json' } }
    )
    console.log('response',response.data)
    return response.data
}

export interface VerifySignupPinParams {
    email: string;
    pin: string;
}

export interface VerifySignupPinResult {
    signupToken: string;
}

/**
 * POST /signup/verify-pin — on success returns a signed 5-minute signup JWT
 * that the wizard must pass as Bearer token on /freetrial. On wrong/expired
 * PIN the backend returns 401.
 */
export const verifySignupPin = async (
    params: VerifySignupPinParams
): Promise<VerifySignupPinResult> => {
    // if (SIGNUP_MOCK) {
    //     if (params.pin === '1234') {
    //         return mockDelay({ signupToken: 'mock-jwt-' + Date.now() })
    //     }
    //     // Simulate the server's 401 for wrong PIN.
    //     await mockDelay(null, 250)
    //     throw mockHttpError(401, 'Invalid or expired signup PIN')
    // }

    const response = await axios.post(
        `${API_NODE}/signup/verify-pin`,
        { email: params.email, pinCode: params.pin },
        { headers: { 'Content-Type': 'application/json' } }
    )
    console.log('response',response.data)
    return response.data.data
}

export interface RegisterFreetrialPerson {
    name: string;
    email: string;
    team: string;
    managerEmail: string | null;
    you?: boolean;
}

export interface RegisterFreetrialCompany {
    name: string;
    country: string;
    timezone: number;
    cadence: 'light' | 'balanced' | 'intense';
    delivery_time: string;
    plan_tier: 'free';
    brand_color: string;
    brand_logo_url: string | null;
    hashtag: { [id: string]: { name: string; name_th: string } };
    onboarding_completed: true;
}

export interface RegisterFreetrialPayload {
    company: RegisterFreetrialCompany;
    core_values: Array<{ label: string }>;
    people: RegisterFreetrialPerson[];
    selected_reward_ids: string[];
    admin: { email: string; firstname?: string; lastname?: string };
}

export interface RegisterFreetrialParams {
    payload: RegisterFreetrialPayload;
    signupToken: string;
}

export interface RegisterFreetrialResult {
    customToken: string;
    uid: string;
    company: string;
}

/**
 * POST /freetrial (Bearer signup JWT) — extended payload. Backend verifies
 * the signup token, atomically provisions the company, superadmin user,
 * hashtag/core values, rewards_company (filtered by selected_reward_ids),
 * teams, member user records + user_invite rows, queues invite emails, and
 * returns a Firebase custom token for `signInWithCustomToken`.
 */
export const registerFreetrial = async (
    params: RegisterFreetrialParams
): Promise<RegisterFreetrialResult> => {
    // if (SIGNUP_MOCK) {
    //     return mockDelay({
    //         customToken: 'mock-custom-token',
    //         uid: 'mock-uid',
    //         company: 'mock-company-' + Date.now(),
    //     })
    // }
    const response = await axios.post(`${API_NODE}/signup/freetrial`, params.payload, {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${params.signupToken}`,
        },
    })
    return response.data
}

export interface ProUpgradeRequestParams {
    company: string
    company_name: string
    requester_email: string
    requester_name: string
    seats?: number
    message?: string
}

/**
 * POST /billing/upgrade-request — sends a Pro plan upgrade request from a
 * free-tier company to sales. Backend persists the request and notifies the
 * sales team. Until the endpoint is live, returns a mock success response so
 * the UI can ship independently.
 */
export const requestProUpgrade = async (
    params: ProUpgradeRequestParams
): Promise<{ ok: true }> => {
    if (SIGNUP_MOCK) {
        return mockDelay({ ok: true })
    }

    await axios.post(`${API_NODE}/billing/upgrade-request`, params, { headers: publicJsonHeaders })
    return { ok: true }
}

export const getEngagementIndexDay = async (company:string, startDate:string, endDate:string) => {
    try {
        const response = await postWithAuth(`https://getengagementindexday1row-bv43tjyzrq-uc.a.run.app`, {
            "where": [
              {"field": "company", "operator": "==", "value": company},
              {"field": "date", "operator": ">=", "value": startDate},
              {"field": "date", "operator": "<=", "value": endDate},
            ]
          })
        if (!response.data || Object.keys(response.data).length === 0) {
            return {};
        }
        
        // console.log('Engagement Index Day Data:', processEngagementData(response.data))
        return processEngagementData(response.data);
    } catch (error) {
        console.log('Error fetching engagement index day:', error);
        throw error;
    }
}
